// File: clientapp/src/pages/Home.jsx
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Typography,
    Grid,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat'; // import Chat icon
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PageLayout from '../components/PageLayout';
import { AuthContext } from '../contexts/AuthContext';

/**
 * Home Page
 * - Uses PageLayout but omits breadcrumb/title for a cleaner dashboard.
 * - Shows large "cards" for Documents and Workflow tasks if the user has the relevant features.
 */

export default function Home() {
    const navigate = useNavigate();
    const { features } = useContext(AuthContext);

    // Adjust these to match your DB feature names.
    const canUploadDocs = features.includes('Upload Docs for Classification');
    const canManageWorkflow = features.includes('Manage My Workflow Tasks');
    const canUseAssistant = features.includes('Use AI Assistant');

    // Reusable card for quick actions
    const QuickActionCard = ({
        icon,
        title,
        description,
        onClick,
        accentColor = '#B74140',
    }) => {
        return (
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
                <CardActionArea onClick={onClick} sx={{ p: 2, textAlign: 'center' }}>
                    {/* Example icon styling with accent color */}
                    <CardMedia
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: 50,
                            color: accentColor,
                            mb: 1,
                        }}
                    >
                        {icon}
                    </CardMedia>
                    <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {description}
                        </Typography>
                    </CardContent>
                </CardActionArea>
            </Card>
        );
    };

    return (
        <PageLayout
            /* We omit all header props for a minimal home page */
            breadcrumb={[]}
            showBack={false}
            showClose={false}
            icon={null}
            title=""      // No title
            subTitle=""   // No subtitle
        >
          <Container sx={{ mt: 2 }}>
            {/* Main content: grid of quick-action cards */}
            <Box sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            {/* Documents Card */}
                            {canUploadDocs && (
                                <Grid item xs={6} sm={4} md={3}>
                                    <QuickActionCard
                                        icon={<InsertDriveFileIcon fontSize="inherit" />}
                                        title="Documents"
                                        description="Upload or view document history"
                                        onClick={() => {
                                            // For example, navigate to your Documents page or open a modal
                                            navigate('/documents');
                                        }}
                                    />
                                </Grid>
                            )}

                            {/* Workflow / Tasks Card */}
                            {canManageWorkflow && (
                                   <Grid item xs={6} sm={4} md={3}>
                                    <QuickActionCard
                                        icon={<AssignmentTurnedInIcon fontSize="inherit" />}
                                        title="My Tasks"
                                        description="Manage your assigned workflow tasks"
                                        onClick={() => {
                                            // For example, navigate to your tasks page
                                            navigate('/tasks');
                                        }}
                                    />
                                </Grid>
                            )}

                            {/* Assistant Card */}
                            {canUseAssistant && (
                                  <Grid item xs={6} sm={4} md={3}>
                                    <QuickActionCard
                                        icon={<ChatIcon fontSize="inherit" />}
                                        title="Assistant"
                                        description="Ask questions or get help"
                                        onClick={() => {
                                            navigate('/assistant');
                                        }}
                                
                                    />
                                </Grid>
                            )}

                            {/* Add more cards here if needed, using the same pattern */}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
           </Container>
        </PageLayout>
    );
}
